import {
  URL_HELP_CENTER_ES,
  URL_MARKETPLACE_LOGGED_OUT,
  URL_SHOP,
} from 'services/url/constants'
import { getPrimary } from 'services/languages'
import { ES } from 'services/languages/constants'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef } from 'react'
import { List } from 'immutable'
import _partial from 'lodash/partial'
import _get from 'lodash/get'
import ImmutablePropTypes from 'react-immutable-proptypes'
import Button, { TYPES } from 'components/Button'
import Menu from 'components/Menu'
import UserAvatar from 'components/UserAvatar'
import ToolTipArrow, { DIRECTION_UP } from 'components/ToolTipArrow'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import { get as getConfig } from 'config'
import { getUserAvatarName } from 'services/user'
import {
  isAccount,
  isAccountLanguage,
  isContact,
  isManageProfilesPage,
  isAmbassadorsPage,
} from 'services/url'
import { ROLE_ID_AMBASSADOR } from 'services/auth-access'

const { features, marketplaceUrl } = getConfig()
const EMPTY_MENU = List()

export function getMenuItemStructure (props) {
  const {
    user,
    menu: menuProp,
    toggleUserMenuVisible,
    auth,
  } = props
  const primaryLang = getPrimary(user.getIn(['data', 'language'], List()))
  const langIsEs = primaryLang === ES
  return menuProp.reduce((reduction, item) => {
    // don't render the language select link if there is a feature toggle
    // @TODO: uncomment feature toggle when appropriate
    // @NOTE: I think this toggle may be dangerous to turn off and remove the entire user menu.
    if (!features.languageSelect) {
      return reduction
    }
    if (!features.languageSelectDialog && isAccountLanguage(item.get('url'))) {
      return reduction
    }

    if (isAmbassadorsPage(item.get('url'))
      && (!features.ambassadorPayouts || !auth.get('roleIds').includes(ROLE_ID_AMBASSADOR))) {
      return reduction
    }

    const menuItem = item.withMutations((mutatableItem) => {
      const isContactUrl = isContact(mutatableItem.get('url'))
      mutatableItem.set('user-menu-item', true)

      if (mutatableItem.get('border')) {
        mutatableItem.set('user-menu-border', true)
      }

      // create a link to the marketplace account area
      if (item.get('url') === URL_MARKETPLACE_LOGGED_OUT) {
        mutatableItem
          .set('url', `${URL_SHOP}?redirect=${encodeURIComponent(`${marketplaceUrl}/account`)}`)
          .set('onClick', () => toggleUserMenuVisible())
      }

      if (
        isAccount(mutatableItem.get('url'))
        || isManageProfilesPage(mutatableItem.get('url'))
        || isAmbassadorsPage(mutatableItem.get('url'))
      ) {
        mutatableItem.set('onClick', () => toggleUserMenuVisible())
      } else if (
        isContactUrl
        && langIsEs
        && _get(features, ['userLanguage', 'esZendeskHelpLink'])
      ) {
        mutatableItem.set('url', URL_HELP_CENTER_ES)
      }

      return mutatableItem
    })
    return reduction.push(menuItem)
  }, List())
}

export function renderMenu (props) {
  const { user, userProfiles, toggleUserMenuVisible } = props
  const portalUrl = user.getIn(['data', 'portal', 'url'])
  const profiles = userProfiles.get('data', List())
  const className = `menu--user menu--profiles-${profiles.size}`

  return (
    <Menu
      location={global.location}
      portalUrl={portalUrl}
      className={className}
      profiles={userProfiles}
      itemClassName="menu-item--user"
      itemLinkClassName="menu-item__link--user"
      toggleUserMenuVisible={toggleUserMenuVisible}
      menu={getMenuItemStructure(props)}
    />
  )
}

export function onClickMenuWrapper (e) {
  e.stopPropagation()
}

export function showUserMenu (
  e,
  toggleUserMenuVisible,
  setNavigationOverlayVisible,
  setNotificationsMenuVisible,
) {
  e.stopPropagation()
  toggleUserMenuVisible()
  setNavigationOverlayVisible(false)
  setNotificationsMenuVisible(false)
}

export function renderUserAvatar (props) {
  const { user } = props

  if (!user.get('data')) {
    return <div className="user-menu__user-avatar-placeholder" />
  }

  const userAvatarName = getUserAvatarName(user.getIn(['data']))
  return (
    <UserAvatar
      path={user.getIn(['data', 'avatar', 'small'], '')}
      className={['user-menu__avatar']}
      name={userAvatarName}
    />
  )
}

export function getClassName (hideUserMenu) {
  const className = ['user-menu__user']
  if (hideUserMenu) {
    className.push('user-menu__user--disabled')
  }
  return className.join(' ')
}

const UserMenu = (props) => {
  const {
    auth,
    menuVisible,
    hideUserMenu,
    toggleUserMenuVisible,
    setNavigationOverlayVisible,
    setNotificationsMenuVisible,
  } = props
  const baseWrapperClass = 'user-menu__menu-wrapper'
  const wrapperClass = menuVisible
    ? `${baseWrapperClass} user-menu__menu-wrapper--visible`
    : baseWrapperClass
  const containerRef = useRef(null)
  const handleClickOutside = useCallback((event) => {
    if (containerRef.current && !containerRef.current.contains(event.target) && menuVisible) {
      toggleUserMenuVisible()
    }
  }, [menuVisible, toggleUserMenuVisible])

  useEffect(() => {
    const handleClick = (event) => {
      handleClickOutside(event)
    }

    document.addEventListener('mousedown', handleClick)

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [handleClickOutside])

  if (auth.get('jwt')) {
    return (
      <div className="user-menu" ref={containerRef}>
        <ToolTipArrow
          direction={DIRECTION_UP}
          activeArrow="user-menu-arrow"
        />
        <Button
          type={TYPES.LINK}
          className={getClassName(hideUserMenu)}
          onClick={
            hideUserMenu
              ? null
              : _partial(
                showUserMenu,
                _partial.placeholder,
                toggleUserMenuVisible,
                setNavigationOverlayVisible,
                setNotificationsMenuVisible,
              )
          }
          aria-haspopup="true"
          aria-controls="user-menu-wrapper"
        >
          {renderUserAvatar(props)}
        </Button>
        {menuVisible ? (
          <div id="user-menu-wrapper" className={wrapperClass} onClick={onClickMenuWrapper}>
            <span className="user-menu__arrow" />
            {renderMenu(props)}
          </div>
        ) : null}
      </div>
    )
  }
  return null
}

UserMenu.props = {
  menu: ImmutablePropTypes.map.isRequired,
  menuVisible: PropTypes.bool.isRequired,
  toggleUserMenuVisible: PropTypes.func.isRequired,
  setNavigationOverlayVisible: PropTypes.func.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  paytrack: ImmutablePropTypes.map.isRequired,
  getUserData: PropTypes.func.isRequired,
  hideUserMenu: PropTypes.bool.isRequired,
  setOverlayDialogVisible: PropTypes.func.isRequired,
}

export default connectRedux(
  (state) => ({
    auth: state.auth,
    user: state.user,
    paytrack: state.paytrack,
    userAccount: state.userAccount,
    userProfiles: state.userProfiles,
    menuVisible: state.header.get('userMenuVisible'),
    menu: state.menu.getIn(['data', 'user'], EMPTY_MENU),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      toggleUserMenuVisible: actions.header.toggleUserMenuVisible,
      setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
      setNotificationsMenuVisible: actions.header.setNotificationsMenuVisible,
      setNavigationOverlayVisible: actions.header.setNavigationOverlayVisible,
    }
  },
)(UserMenu)
